import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import type { AuthClient } from '../libs/authClient'

interface SessionUser {
  id: string | number
  email: string
  name: string
  username?: string
  role?: string
  image?: string | null
  emailVerified?: boolean
  createdAt?: Date | string
  showNsfw?: boolean
  nsfwBlurByDefault?: boolean
  preferredTags?: string[]
  blacklistedTags?: string[]
  nsfwVerifiedAt?: Date | string | null
  twitchUsername?: string
  discordUsername?: string
}

interface AuthUser {
  id: string
  email: string
  name: string
  username?: string
  role: string
  image?: string | null
  emailVerified?: boolean
  createdAt?: Date
  showNsfw?: boolean
  nsfwBlurByDefault?: boolean
  preferredTags?: string[]
  blacklistedTags?: string[]
  nsfwVerifiedAt?: Date | null
  twitchUsername?: string
  discordUsername?: string
}

export interface LinkedAuthAccount {
  id: string
  accountId: string
  providerId: string
  scopes: string[]
  createdAt: Date | string
  updatedAt: Date | string
}

let authClientInstance: AuthClient | null = null
let authBaseUrl: string = ''

function normalizeOptionalDate(value: Date | string | null | undefined): Date | null | undefined {
  if (value == null || value instanceof Date) return value

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function setAuthClient(client: AuthClient | null, baseUrl: string = ''): void {
  authClientInstance = client
  authBaseUrl = baseUrl
}

export const useAuthStore = defineStore('auth', () => {
  const user: Ref<AuthUser | null> = ref(null)
  // When this browser's session began, i.e. the last sign-in here.
  const sessionStartedAt: Ref<Date | null> = ref(null)
  const loading: Ref<boolean> = ref(false)
  const sessionInitialized: Ref<boolean> = ref(false)
  let pendingSessionRequest: Promise<boolean> | null = null
  let sessionGeneration = 0

  const isAuthenticated = computed<boolean>(() => user.value !== null)

  const galleryViewer = computed(() => ({
    id: user.value?.id,
    showNsfw: user.value?.showNsfw === true && Boolean(user.value.nsfwVerifiedAt),
  }))

  function invalidateSessionRequest(): void {
    sessionGeneration += 1
    pendingSessionRequest = null
    loading.value = false
  }

  function setSessionUser(sessionUser: SessionUser | null): void {
    invalidateSessionRequest()
    applySessionUser(sessionUser)
  }

  function applySessionUser(sessionUser: SessionUser | null): void {
    sessionInitialized.value = true

    if (!sessionUser) {
      user.value = null
      sessionStartedAt.value = null
      return
    }

    user.value = {
      id: String(sessionUser.id),
      email: String(sessionUser.email),
      name: String(sessionUser.name),
      username: sessionUser.username,
      role: sessionUser.role || 'USER',
      image: sessionUser.image,
      emailVerified: sessionUser.emailVerified,
      createdAt: normalizeOptionalDate(sessionUser.createdAt) ?? undefined,
      showNsfw: sessionUser.showNsfw,
      nsfwBlurByDefault: sessionUser.nsfwBlurByDefault,
      preferredTags: sessionUser.preferredTags,
      blacklistedTags: sessionUser.blacklistedTags,
      nsfwVerifiedAt: normalizeOptionalDate(sessionUser.nsfwVerifiedAt),
      twitchUsername: sessionUser.twitchUsername,
      discordUsername: sessionUser.discordUsername,
    }
  }

  async function fetchSession(options: { force?: boolean } = {}): Promise<boolean> {
    if (!authClientInstance) {
      loading.value = false
      return false
    }

    if (sessionInitialized.value && !options.force) return true
    if (pendingSessionRequest) return pendingSessionRequest

    const client = authClientInstance
    const generation = sessionGeneration
    const request = (async (): Promise<boolean> => {
      loading.value = true

      try {
        const session = await client.getSession()
        if (generation !== sessionGeneration || session.error) {
          return false
        }

        applySessionUser(session.data?.user ? (session.data.user as SessionUser) : null)
        if (session.data?.user) sessionStartedAt.value = normalizeOptionalDate(session.data.session.createdAt) ?? null
        return true
      } catch {
        return false
      } finally {
        if (generation === sessionGeneration) {
          sessionInitialized.value = true
          loading.value = false
        }
      }
    })()

    pendingSessionRequest = request

    try {
      return await request
    } finally {
      if (pendingSessionRequest === request) pendingSessionRequest = null
    }
  }

  async function signInWithEmail(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    if (!authClientInstance) {
      return { success: false, error: 'Auth client not initialized' }
    }

    try {
      invalidateSessionRequest()
      const result = await authClientInstance.signIn.email({ email, password })
      if (result.error) {
        return { success: false, error: result.error.message }
      }
      const sessionLoaded = await fetchSession({ force: true })
      if (!sessionLoaded) {
        return { success: false, error: 'Signed in, but the session could not be loaded. Please try again.' }
      }
      return { success: true }
    } catch (error) {
      return { success: false, error: String(error) }
    }
  }

  async function signInWithSocial(provider: 'discord' | 'twitch', callbackURL: string = window.location.origin): Promise<void> {
    if (!authClientInstance) {
      throw new Error('Auth client not initialized')
    }

    // Return failed OAuth attempts to a screen that can explain the error.
    const errorUrl = new URL('/login', window.location.origin)
    errorUrl.searchParams.set('provider', provider)

    const result = await authClientInstance.signIn.social({
      provider,
      callbackURL,
      errorCallbackURL: errorUrl.toString(),
    })
    if (result.error) {
      throw new Error(result.error.message || `Unable to sign in with ${provider}`)
    }
  }

  async function linkSocial(provider: 'discord' | 'twitch'): Promise<void> {
    if (!authClientInstance) {
      throw new Error('Auth client not initialized')
    }

    // Build the callback URL (current page without error params)
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('error')
    currentUrl.searchParams.delete('error_description')
    currentUrl.searchParams.delete('provider')

    // Build the error callback URL (current page with provider param)
    // Better-Auth will append &error=<code> to this URL on failure
    const errorUrl = new URL(currentUrl)
    errorUrl.searchParams.set('provider', provider)

    const result = await authClientInstance.linkSocial({
      provider,
      callbackURL: currentUrl.toString(),
      errorCallbackURL: errorUrl.toString(),
    })
    if (result.error) {
      throw new Error(result.error.message || `Unable to connect ${provider}`)
    }
  }

  async function listLinkedAccounts(): Promise<LinkedAuthAccount[]> {
    if (!authClientInstance) {
      throw new Error('Auth client not initialized')
    }

    const result = await authClientInstance.listAccounts()
    if (result.error) {
      throw new Error(result.error.message || 'Unable to load linked accounts')
    }

    return (result.data ?? []).map((account) => ({
      id: account.id,
      accountId: account.accountId,
      providerId: account.providerId,
      scopes: account.scopes,
      createdAt: account.createdAt,
      updatedAt: account.updatedAt,
    }))
  }

  async function unlinkSocial(provider: 'discord' | 'twitch'): Promise<void> {
    if (!authClientInstance) {
      throw new Error('Auth client not initialized')
    }

    // Better Auth unlinks by the local account id, not by provider (1.7).
    const account = (await listLinkedAccounts()).find((linked) => linked.providerId === provider)
    if (!account) {
      throw new Error(`No ${provider} account is connected`)
    }
    const result = await authClientInstance.unlinkAccount({ accountId: account.id })
    if (result.error) {
      throw new Error(result.error.message || `Unable to disconnect ${provider}`)
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!authClientInstance) {
      throw new Error('Auth client not initialized')
    }

    const result = await authClientInstance.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    })
    if (result.error) {
      throw new Error(result.error.message || 'Unable to change password')
    }
  }

  async function signUp(email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> {
    if (!authClientInstance) {
      return { success: false, error: 'Auth client not initialized' }
    }

    try {
      invalidateSessionRequest()
      const result = await authClientInstance.signUp.email({ email, password, name })
      if (result.error) {
        return { success: false, error: result.error.message }
      }
      const sessionLoaded = await fetchSession({ force: true })
      if (!sessionLoaded) {
        return { success: false, error: 'Account created, but the session could not be loaded. Please try signing in.' }
      }
      return { success: true }
    } catch (error) {
      return { success: false, error: String(error) }
    }
  }

  async function signOut(): Promise<void> {
    // Drop a pending session load so it cannot re-apply this account, but keep
    // the user (and its scoped queries) until the server has ended the
    // session. Clearing first switches ungated private queries to the
    // anonymous scope while the cookie is still valid, so they refetch the old
    // account's data into it.
    invalidateSessionRequest()
    try {
      if (authClientInstance) await authClientInstance.signOut()
    } finally {
      setSessionUser(null)
    }
  }

  function clearAuthState(): void {
    setSessionUser(null)
  }

  /** Bind an HTTP failure to the session that sent the request. */
  function captureUnauthorizedHandler(): () => void {
    const generation = sessionGeneration
    const sessionUser = user.value
    const role = sessionUser?.role
    return (): void => {
      if (generation === sessionGeneration && user.value === sessionUser && user.value?.role === role) {
        clearAuthState()
      }
    }
  }

  /** `client` picks the reset email and the page its link opens: the shop, the main site or the S3T studio. */
  async function forgotPassword(
    email: string,
    client: 'main' | 'shop' | 'streamer' = 'shop',
  ): Promise<{ success: boolean; error?: string }> {
    if (!authBaseUrl) {
      return { success: false, error: 'Auth base URL not initialized' }
    }

    try {
      const response = await fetch(`${authBaseUrl}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, client }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        return { success: false, error: data.message || 'Request failed' }
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: String(error) }
    }
  }

  async function resetPassword(token: string, password: string): Promise<{ success: boolean; error?: string }> {
    if (!authBaseUrl) {
      return { success: false, error: 'Auth base URL not initialized' }
    }

    try {
      const response = await fetch(`${authBaseUrl}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        return { success: false, error: data.message || 'Reset failed' }
      }

      return { success: true }
    } catch (error) {
      return { success: false, error: String(error) }
    }
  }

  return {
    user,
    sessionStartedAt,
    loading,
    sessionInitialized,
    isAuthenticated,
    galleryViewer,
    setSessionUser,
    fetchSession,
    signInWithEmail,
    signInWithSocial,
    linkSocial,
    listLinkedAccounts,
    unlinkSocial,
    changePassword,
    signUp,
    signOut,
    forgotPassword,
    resetPassword,
    clearAuthState,
    captureUnauthorizedHandler,
  }
})
